import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { DownloadOutlined, LeftOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { useAuthSession } from '../lib/session'
import { useToast } from '../contexts/ToastContext'
import {
  useAdvisoryMappingDetails,
  useExportAccountMappingMutation,
  useExportAdvisoryMappingMutation,
  useExportMultiEntityOwnershipMutation,
  useExportRelatedContactMutation,
  useExportRelatedLoanAccountMutation,
  useUploadAdvisoryMappingMutation,
  useUpsertAccountMappingMutation,
  useUpsertMultiEntityOwnershipMutation,
  useUpsertRelatedContactsMutation,
  useUpsertRelatedLoanAccountsMutation,
  useUpsertUserAccessMutation,
} from '../hooks/useAdvisoryMapping'
import { fileToBase64 } from '../lib/files'
import { PlatformType, UserTypes } from '../types/authorization'
import type {
  AccountMappingRow,
  AccountMappingTemplate,
  MultiEntityOwnershipRow,
  RelatedContactRow,
  RelatedLoanAccountRow,
  UserAccessRow,
} from '../types/advisoryMapping'

const { Text } = Typography

type KeyedRow<T> = {
  key: string
  row: T
}

type CsvFiles = {
  advisoryMapping: File | null
  accountMapping: File | null
  multiEntityOwnership: File | null
  relatedContact: File | null
  relatedLoanAccount: File | null
}

function makeRowKey(prefix: string, idx: number) {
  return `${prefix}-${idx}-${Math.random().toString(36).slice(2, 8)}`
}

function toKeyed<T>(prefix: string, rows: T[]): KeyedRow<T>[] {
  return rows.map((row, idx) => ({ key: makeRowKey(prefix, idx), row }))
}

function markDeleted<T extends { isDeleted?: boolean; isModified?: boolean }>(row: T): T {
  return { ...row, isDeleted: true, isModified: true }
}

function hasSectionChanges<T extends { isDeleted?: boolean; isModified?: boolean; isNew?: boolean }>(rows: KeyedRow<T>[]) {
  return rows.some((item) => item.row.isDeleted || item.row.isModified || item.row.isNew)
}

function toCsvDownload(base64Content: string, fileName: string) {
  const link = document.createElement('a')
  link.href = `data:text/csv;base64,${base64Content}`
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function toYmd(value: string | null) {
  if (!value) return null
  const date = dayjs(value)
  if (!date.isValid()) return null
  return date.format('YYYY-MM-DD')
}

function mapAtRcn(template: AccountMappingTemplate) {
  return (
    template.atRcn ||
    (template.accountType && template.reportCodeName
      ? `${template.accountType} (${template.reportCodeName})`
      : template.accountType)
  )
}

export default function AdvisoryMappingDetail() {
  const { platformType = '0', platformId = '' } = useParams<{ platformType: string; platformId: string }>()
  const platformTypeId = Number(platformType)
  const navigate = useNavigate()
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''
  const { addToast } = useToast()

  const [accountRows, setAccountRows] = useState<KeyedRow<AccountMappingRow>[]>([])
  const [multiRows, setMultiRows] = useState<KeyedRow<MultiEntityOwnershipRow>[]>([])
  const [contactRows, setContactRows] = useState<KeyedRow<RelatedContactRow>[]>([])
  const [loanRows, setLoanRows] = useState<KeyedRow<RelatedLoanAccountRow>[]>([])
  const [accessRows, setAccessRows] = useState<KeyedRow<UserAccessRow>[]>([])

  const [userAccessSearch, setUserAccessSearch] = useState('')
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [loanModalOpen, setLoanModalOpen] = useState(false)
  const [selectedLoanAccounts, setSelectedLoanAccounts] = useState<string[]>([])
  const [csvFiles, setCsvFiles] = useState<CsvFiles>({
    advisoryMapping: null,
    accountMapping: null,
    multiEntityOwnership: null,
    relatedContact: null,
    relatedLoanAccount: null,
  })

  const detailsQuery = useAdvisoryMappingDetails(platformId, clientId)
  const upsertAccountMutation = useUpsertAccountMappingMutation(platformId, clientId)
  const upsertMultiMutation = useUpsertMultiEntityOwnershipMutation(platformId, clientId)
  const upsertContactMutation = useUpsertRelatedContactsMutation(platformId, clientId)
  const upsertLoanMutation = useUpsertRelatedLoanAccountsMutation(platformId, clientId)
  const upsertAccessMutation = useUpsertUserAccessMutation(platformId, clientId)
  const uploadMutation = useUploadAdvisoryMappingMutation(platformId, clientId)

  const exportAdvisoryMutation = useExportAdvisoryMappingMutation()
  const exportAccountMutation = useExportAccountMappingMutation()
  const exportMultiMutation = useExportMultiEntityOwnershipMutation()
  const exportContactMutation = useExportRelatedContactMutation()
  const exportLoanMutation = useExportRelatedLoanAccountMutation()

  useEffect(() => {
    if (!detailsQuery.data) return
    setAccountRows(toKeyed('account', detailsQuery.data.accountMapping))
    setMultiRows(toKeyed('multi', detailsQuery.data.multiEntityOwnership))
    setContactRows(toKeyed('contact', detailsQuery.data.relatedContact))
    setLoanRows(toKeyed('loan', detailsQuery.data.relatedLoanAccount))
    setAccessRows(toKeyed('access', detailsQuery.data.userAccess))
    setSelectedLoanAccounts(detailsQuery.data.relatedLoanAccount.filter((row) => !row.isDeleted).map((row) => row.loanAccount))
  }, [detailsQuery.data])

  const hideUserAccess = useMemo(() => {
    const isAdmin = session?.currentUserTypeId === UserTypes.Admin
    const isClientAdmin = session?.currentUserTypeId === UserTypes.ClientAdmin
    const isClientTypeStandard = session?.currentClientTypeId === 1
    const hasAdvisor = Boolean(session?.advisorId && session.advisorId !== '00000000-0000-0000-0000-000000000000')
    return !isAdmin && !isClientAdmin && isClientTypeStandard && hasAdvisor
  }, [session?.advisorId, session?.currentClientTypeId, session?.currentUserTypeId])

  const accountTemplates = useMemo(() => detailsQuery.data?.accountMappingList || [], [detailsQuery.data?.accountMappingList])
  const accountsMaster = useMemo(() => detailsQuery.data?.accountsMaster || [], [detailsQuery.data?.accountsMaster])

  const accountOptions = useMemo(() => accountTemplates.map((row) => ({ label: row.account, value: row.account })), [accountTemplates])
  const atRcnOptions = useMemo(() => accountsMaster.map((row) => ({ label: row.atRcn, value: row.atRcn })), [accountsMaster])

  const filteredAccessRows = useMemo(() => {
    if (!userAccessSearch.trim()) return accessRows
    const q = userAccessSearch.trim().toLowerCase()
    return accessRows.filter((item) => item.row.name.toLowerCase().includes(q) || item.row.email.toLowerCase().includes(q))
  }, [accessRows, userAccessSearch])

  const activeAccountRows = useMemo(() => accountRows.filter((item) => !item.row.isDeleted), [accountRows])
  const activeMultiRows = useMemo(() => multiRows.filter((item) => !item.row.isDeleted), [multiRows])
  const activeContactRows = useMemo(() => contactRows.filter((item) => !item.row.isDeleted), [contactRows])
  const activeLoanRows = useMemo(() => loanRows.filter((item) => !item.row.isDeleted), [loanRows])
  const activeAccessRows = useMemo(() => filteredAccessRows.filter((item) => !(item.row as { isDeleted?: boolean }).isDeleted), [filteredAccessRows])

  const hasAccountChanges = hasSectionChanges(accountRows)
  const hasMultiChanges = hasSectionChanges(multiRows)
  const hasContactChanges = hasSectionChanges(contactRows)
  const hasLoanChanges = hasSectionChanges(loanRows)
  const hasAccessChanges = hasSectionChanges(accessRows)

  const updateByKey = <T,>(setter: Dispatch<SetStateAction<KeyedRow<T>[]>>, key: string, updater: (row: T) => T) => {
    setter((prev) => prev.map((item) => (item.key === key ? { ...item, row: updater(item.row) } : item)))
  }

  const accountColumns: ColumnsType<KeyedRow<AccountMappingRow>> = [
    {
      title: 'Account',
      key: 'account',
      render: (_value, item) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          value={item.row.account || undefined}
          options={accountOptions}
          onChange={(value) => {
            const selectedTemplate = accountTemplates.find((template) => template.account === value)
            updateByKey(setAccountRows, item.key, (row) => ({
              ...row,
              account: value,
              isModified: true,
              isOverriden: 1,
              plGroup: selectedTemplate?.plGroup || row.plGroup,
              balanceSheetType: selectedTemplate?.balanceSheetType || row.balanceSheetType,
              accountType: selectedTemplate?.accountType || row.accountType,
              reportCodeName: selectedTemplate?.reportCodeName || row.reportCodeName,
              atRcn: selectedTemplate ? mapAtRcn(selectedTemplate) : row.atRcn,
              accountMappingId: selectedTemplate?.accountMappingId || row.accountMappingId,
            }))
          }}
        />
      ),
    },
    {
      title: 'Report Code Name',
      key: 'atRcn',
      render: (_value, item) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          value={item.row.atRcn || undefined}
          options={atRcnOptions}
          onChange={(value) => {
            const master = accountsMaster.find((entry) => entry.atRcn === value)
            const selectedTemplate = accountTemplates.find((template) => template.account === item.row.account)
            updateByKey(setAccountRows, item.key, (row) => ({
              ...row,
              atRcn: value,
              isModified: true,
              isOverriden: 1,
              plGroup: master?.plGroup || row.plGroup,
              balanceSheetType: master?.balanceSheetType || row.balanceSheetType,
              accountType: selectedTemplate?.accountType || row.accountType,
              reportCodeName: selectedTemplate?.reportCodeName || row.reportCodeName,
              accountMappingId: selectedTemplate?.accountMappingId || row.accountMappingId,
            }))
          }}
        />
      ),
    },
    { title: 'P&L Group', dataIndex: ['row', 'plGroup'], key: 'plGroup' },
    { title: 'Balance Sheet Type', dataIndex: ['row', 'balanceSheetType'], key: 'balanceSheetType' },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_value, item) => (
        <Button danger type="link" onClick={() => updateByKey(setAccountRows, item.key, (row) => markDeleted({ ...row, isOverriden: 0 }))}>
          Delete
        </Button>
      ),
    },
  ]

  const multiColumns: ColumnsType<KeyedRow<MultiEntityOwnershipRow>> = [
    {
      title: 'Shareholder Name',
      key: 'shareholderName',
      render: (_value, item) => (
        <Input value={item.row.shareholderName} onChange={(event) => updateByKey(setMultiRows, item.key, (row) => ({ ...row, shareholderName: event.target.value, isModified: true }))} />
      ),
    },
    {
      title: 'Shareholder Email',
      key: 'shareholderEmail',
      render: (_value, item) => (
        <Input value={item.row.shareholderEmail} onChange={(event) => updateByKey(setMultiRows, item.key, (row) => ({ ...row, shareholderEmail: event.target.value, isModified: true }))} />
      ),
    },
    {
      title: '% Share',
      key: 'shares',
      render: (_value, item) => (
        <Input type="number" value={String(item.row.shares ?? 0)} onChange={(event) => updateByKey(setMultiRows, item.key, (row) => ({ ...row, shares: Number(event.target.value || 0), isModified: true }))} />
      ),
    },
    {
      title: 'Effective Start Date',
      key: 'startDate',
      render: (_value, item) => (
        <DatePicker value={item.row.startDate ? dayjs(item.row.startDate) : null} onChange={(value) => updateByKey(setMultiRows, item.key, (row) => ({ ...row, startDate: value ? value.format('YYYY-MM-DD') : null, isModified: true }))} />
      ),
    },
    {
      title: 'Effective End Date',
      key: 'endDate',
      render: (_value, item) => (
        <DatePicker value={item.row.endDate ? dayjs(item.row.endDate) : null} onChange={(value) => updateByKey(setMultiRows, item.key, (row) => ({ ...row, endDate: value ? value.format('YYYY-MM-DD') : null, isModified: true }))} />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_value, item) => (
        <Button danger type="link" onClick={() => updateByKey(setMultiRows, item.key, (row) => markDeleted(row))}>
          Delete
        </Button>
      ),
    },
  ]

  const contactColumns: ColumnsType<KeyedRow<RelatedContactRow>> = [
    {
      title: 'Related Contact',
      key: 'contactName',
      render: (_value, item) => (
        <Input value={item.row.contactName} onChange={(event) => updateByKey(setContactRows, item.key, (row) => ({ ...row, contactName: event.target.value, isModified: true }))} />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_value, item) => (
        <Button danger type="link" onClick={() => updateByKey(setContactRows, item.key, (row) => markDeleted(row))}>
          Delete
        </Button>
      ),
    },
  ]

  const loanColumns: ColumnsType<KeyedRow<RelatedLoanAccountRow>> = [
    { title: 'Related Loan Account', dataIndex: ['row', 'loanAccount'], key: 'loanAccount' },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_value, item) => (
        <Button danger type="link" onClick={() => updateByKey(setLoanRows, item.key, (row) => markDeleted(row))}>
          Delete
        </Button>
      ),
    },
  ]

  const accessColumns: ColumnsType<KeyedRow<UserAccessRow>> = [
    {
      title: '',
      key: 'canAccess',
      width: 80,
      render: (_value, item) => (
        <Checkbox
          checked={item.row.canAccess}
          disabled={Boolean(item.row.isXeroUser)}
          onChange={(event) => updateByKey(setAccessRows, item.key, (row) => ({ ...row, canAccess: event.target.checked, isModified: true }))}
        />
      ),
    },
    { title: 'Name', dataIndex: ['row', 'name'], key: 'name' },
    { title: 'Email', dataIndex: ['row', 'email'], key: 'email' },
  ]

  const saveAccountMapping = async () => {
    if (!hasAccountChanges) return
    try {
      await upsertAccountMutation.mutateAsync(accountRows.map((item) => item.row))
      addToast('Account mapping saved', 'success')
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save account mapping', 'error')
    }
  }

  const saveMultiEntity = async () => {
    if (!hasMultiChanges) return
    try {
      const payload = multiRows.map((item) => ({ ...item.row, startDate: toYmd(item.row.startDate), endDate: toYmd(item.row.endDate) }))
      await upsertMultiMutation.mutateAsync(payload)
      addToast('Multi entity ownership saved', 'success')
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save multi entity ownership', 'error')
    }
  }

  const saveRelatedContacts = async () => {
    if (!hasContactChanges) return
    try {
      await upsertContactMutation.mutateAsync(contactRows.map((item) => item.row))
      addToast('Related contacts saved', 'success')
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save related contacts', 'error')
    }
  }

  const saveRelatedLoans = async () => {
    if (!hasLoanChanges) return
    try {
      await upsertLoanMutation.mutateAsync(loanRows.map((item) => item.row))
      addToast('Related loan accounts saved', 'success')
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save related loan accounts', 'error')
    }
  }

  const saveUserAccess = async () => {
    if (!hasAccessChanges) return
    try {
      await upsertAccessMutation.mutateAsync(accessRows.map((item) => item.row))
      addToast('User access saved', 'success')
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save user access', 'error')
    }
  }

  const openLoanModal = () => {
    setSelectedLoanAccounts(loanRows.filter((item) => !item.row.isDeleted).map((item) => item.row.loanAccount))
    setLoanModalOpen(true)
  }

  const saveLoanModal = async () => {
    const current = loanRows.map((item) => item.row)
    const selected = selectedLoanAccounts

    const nextRows = current.map((row) => ({ ...row, isDeleted: !selected.includes(row.loanAccount), isModified: true }))

    selected.forEach((loanAccount) => {
      if (!current.some((row) => row.loanAccount === loanAccount)) {
        nextRows.push({ loanAccount, tenant: platformId, isNew: true, isModified: true, isDeleted: false })
      }
    })

    try {
      await upsertLoanMutation.mutateAsync(nextRows)
      addToast('Related loan accounts updated', 'success')
      setLoanModalOpen(false)
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update related loan accounts', 'error')
    }
  }

  const onFileChange = (key: keyof CsvFiles, file: File | null) => {
    setCsvFiles((prev) => ({ ...prev, [key]: file }))
  }

  const uploadFiles = async () => {
    try {
      const filesToCheck: Array<{ key: keyof CsvFiles; file: File | null }> = [
        { key: 'advisoryMapping', file: csvFiles.advisoryMapping },
        { key: 'multiEntityOwnership', file: csvFiles.multiEntityOwnership },
        { key: 'relatedContact', file: csvFiles.relatedContact },
        { key: 'relatedLoanAccount', file: csvFiles.relatedLoanAccount },
      ]
      if (platformTypeId === PlatformType.Xero) {
        filesToCheck.push({ key: 'accountMapping', file: csvFiles.accountMapping })
      }

      const converted: Partial<Record<keyof CsvFiles, string | null>> = {}
      for (const entry of filesToCheck) {
        const file = entry.file
        if (!file) {
          converted[entry.key] = null
          continue
        }

        const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
        if (!isCsv) throw new Error('All files should be in CSV format.')

        converted[entry.key] = await fileToBase64(file)
      }

      await uploadMutation.mutateAsync({
        advisoryMapping: converted.advisoryMapping || null,
        accountMapping: platformTypeId === PlatformType.Xero ? converted.accountMapping || null : null,
        multiEntityOwnership: converted.multiEntityOwnership || null,
        relatedContact: converted.relatedContact || null,
        relatedLoanAccount: converted.relatedLoanAccount || null,
        contentType: 'text/csv',
        clientId,
      })

      addToast('The data has been imported', 'success')
      setImportModalOpen(false)
      setCsvFiles({
        advisoryMapping: null,
        accountMapping: null,
        multiEntityOwnership: null,
        relatedContact: null,
        relatedLoanAccount: null,
      })
      void detailsQuery.refetch()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to import files', 'error')
    }
  }

  const exportAll = async () => {
    if (!platformId) return
    try {
      const tasks: Promise<string>[] = [
        exportAdvisoryMutation.mutateAsync(platformId),
        exportMultiMutation.mutateAsync(platformId),
        exportContactMutation.mutateAsync(platformId),
        exportLoanMutation.mutateAsync(platformId),
      ]
      if (platformTypeId === PlatformType.Xero) tasks.push(exportAccountMutation.mutateAsync(platformId))

      const files = await Promise.all(tasks)
      const f0 = files[0]
      const f1 = files[1]
      const f2 = files[2]
      const f3 = files[3]
      if (f0 != null) toCsvDownload(f0, 'advisory mapping.csv')
      if (f1 != null) toCsvDownload(f1, 'multi entity ownership.csv')
      if (f2 != null) toCsvDownload(f2, 'related contact.csv')
      if (f3 != null) toCsvDownload(f3, 'related loan account.csv')
      if (platformTypeId === PlatformType.Xero && files[4]) toCsvDownload(files[4], 'account mapping.csv')
      addToast('Export complete', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to export mapping data', 'error')
    }
  }

  if (!clientId) return <Alert type="warning" showIcon message="No active client selected" />

  if (detailsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    )
  }

  if (detailsQuery.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load advisory mapping details"
        description={detailsQuery.error instanceof Error ? detailsQuery.error.message : 'Unknown error'}
      />
    )
  }

  if (!detailsQuery.data) return <Empty description="No advisory mapping details found" />

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button icon={<LeftOutlined />} onClick={() => navigate('/advisorymapping')}>Back</Button>
        <h1 className="font-display text-2xl font-bold text-gray-900">{detailsQuery.data.organisationName}</h1>
        <Button icon={<UploadOutlined />} className="di-button-light" onClick={() => setImportModalOpen(true)}>Import</Button>
        <Button
          icon={<DownloadOutlined />}
          className="di-button-light"
          onClick={exportAll}
          loading={exportAdvisoryMutation.isPending || exportMultiMutation.isPending || exportContactMutation.isPending || exportLoanMutation.isPending || exportAccountMutation.isPending}
        >
          Export
        </Button>
      </div>

      <Collapse defaultActiveKey={['account', 'multi', 'contact', 'loan', 'access']}>
        <Collapse.Panel header="Account Mapping" key="account">
          <Space className="pb-3">
            <Button
              icon={<PlusOutlined />}
              className="di-button-light"
              onClick={() => setAccountRows((prev) => [...prev, { key: makeRowKey('account-new', prev.length), row: { account: '', accountType: '', reportCodeName: '', atRcn: '', plGroup: '', balanceSheetType: '', tenant: platformId, isNew: true, isModified: true, isDeleted: false, isOverriden: 0 } }])}
            >
              Add
            </Button>
            <Button className="di-button-light" disabled={!hasAccountChanges} onClick={saveAccountMapping} loading={upsertAccountMutation.isPending}>Save</Button>
          </Space>
          <Table rowKey="key" columns={accountColumns} dataSource={activeAccountRows} pagination={false} scroll={{ x: 960 }} />
        </Collapse.Panel>

        <Collapse.Panel header="Multi Entity Ownership" key="multi">
          <Space className="pb-3">
            <Button
              icon={<PlusOutlined />}
              className="di-button-light"
              onClick={() => setMultiRows((prev) => [...prev, { key: makeRowKey('multi-new', prev.length), row: { shareholderName: '', shareholderEmail: '', shares: 0, startDate: null, endDate: null, tenant: platformId, isNew: true, isModified: true, isDeleted: false } }])}
            >
              Add
            </Button>
            <Button className="di-button-light" disabled={!hasMultiChanges} onClick={saveMultiEntity} loading={upsertMultiMutation.isPending}>Save</Button>
            <Text type="secondary">NOTE: Press Enter upon changing row values to mirror legacy workflow.</Text>
          </Space>
          <Table rowKey="key" columns={multiColumns} dataSource={activeMultiRows} pagination={false} scroll={{ x: 1200 }} />
        </Collapse.Panel>

        <Collapse.Panel header="Related Contact" key="contact">
          <Space className="pb-3">
            <Button
              icon={<PlusOutlined />}
              className="di-button-light"
              onClick={() => setContactRows((prev) => [...prev, { key: makeRowKey('contact-new', prev.length), row: { contactName: '', tenant: platformId, isNew: true, isModified: true, isDeleted: false } }])}
            >
              Add
            </Button>
            <Button className="di-button-light" disabled={!hasContactChanges} onClick={saveRelatedContacts} loading={upsertContactMutation.isPending}>Save</Button>
          </Space>
          <Table rowKey="key" columns={contactColumns} dataSource={activeContactRows} pagination={false} />
        </Collapse.Panel>

        <Collapse.Panel header="Related Loan Account" key="loan">
          <Space className="pb-3">
            <Button icon={<PlusOutlined />} className="di-button-light" onClick={openLoanModal}>Add</Button>
            <Button className="di-button-light" disabled={!hasLoanChanges} onClick={saveRelatedLoans} loading={upsertLoanMutation.isPending}>Save</Button>
          </Space>
          <Table rowKey="key" columns={loanColumns} dataSource={activeLoanRows} pagination={false} />
        </Collapse.Panel>

        {!hideUserAccess ? (
          <Collapse.Panel header="User Access" key="access">
            <Space className="pb-3">
              <Button className="di-button-light" disabled={!hasAccessChanges} onClick={saveUserAccess} loading={upsertAccessMutation.isPending}>Save</Button>
              <Input placeholder="Search users" value={userAccessSearch} onChange={(event) => setUserAccessSearch(event.target.value)} style={{ width: 260 }} />
              <Text type="secondary">NOTE: Changes in Advisory Mapping will reflect in dashboards the next day.</Text>
            </Space>
            <Table rowKey="key" columns={accessColumns} dataSource={activeAccessRows} pagination={false} />
          </Collapse.Panel>
        ) : null}
      </Collapse>

      <Modal title="Import Advisory Mapping" open={importModalOpen} onCancel={() => setImportModalOpen(false)} onOk={uploadFiles} okText="Upload" confirmLoading={uploadMutation.isPending}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Card size="small" title="Advisory Mapping CSV">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFileChange('advisoryMapping', e.target.files?.[0] || null)} />
          </Card>
          {platformTypeId === PlatformType.Xero ? (
            <Card size="small" title="Account Mapping CSV">
              <Input type="file" accept=".csv,text/csv" onChange={(e) => onFileChange('accountMapping', e.target.files?.[0] || null)} />
            </Card>
          ) : null}
          <Card size="small" title="Multi Entity Ownership CSV">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFileChange('multiEntityOwnership', e.target.files?.[0] || null)} />
          </Card>
          <Card size="small" title="Related Contact CSV">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFileChange('relatedContact', e.target.files?.[0] || null)} />
          </Card>
          <Card size="small" title="Related Loan Account CSV">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFileChange('relatedLoanAccount', e.target.files?.[0] || null)} />
          </Card>
        </Space>
      </Modal>

      <Modal title="Modify Related Loan Account" open={loanModalOpen} onCancel={() => setLoanModalOpen(false)} onOk={saveLoanModal} okText="Save" confirmLoading={upsertLoanMutation.isPending}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          value={selectedLoanAccounts}
          onChange={(values) => setSelectedLoanAccounts(values)}
          options={(detailsQuery.data.relatedLoanAccountList || []).slice().sort((a, b) => a.localeCompare(b)).map((loan) => ({ value: loan, label: loan }))}
        />
      </Modal>
    </div>
  )
}
